import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { WarehouseProvider } from './contexts/WarehouseContext';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import { SupportSessionProvider } from './contexts/SupportSessionContext';
import { SupportProvider } from './contexts/SupportContext';
import { Toaster } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { SupportSessionBanner } from './components/SupportSessionBanner';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { TrialSetup } from './pages/TrialSetup';
import { TrialActivated } from './pages/TrialActivated';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { VerifyEmail } from './pages/VerifyEmail';
import { Dashboard } from './pages/Dashboard';
import { Vans } from './pages/Vans';
import { VanDetail } from './pages/VanDetail';
import { MyVehicle } from './pages/MyVehicle';
import { Warehouse } from './pages/Warehouse';
import { WarehouseImport } from './pages/WarehouseImport';
import { Catalogue } from './pages/Catalogue';
import FindDuplicates from './pages/FindDuplicates';
import { InventoryImport } from './pages/InventoryImport';
import { Replenishment } from './pages/Replenishment';
// Activity page removed — redirect handled in routes
import { EventDetail } from './pages/EventDetail';
import { Messages } from './pages/Messages';
import { Team } from './pages/Team';
import { Approvals } from './pages/Approvals';
import { Compare } from './pages/Compare';
import { PurchaseOrders } from './pages/PurchaseOrders';
import { PartRequests } from './pages/PartRequests'; // Phase 5A
import { Quarantine } from './pages/Quarantine'; // Phase 5B
import { JobTimeline } from './pages/JobTimeline'; // Phase 5C
import { JobKits } from './pages/JobKits'; // Phase 6C
import JobTemplates from './pages/JobTemplates'; // Stage 12
import Vendors from './pages/Vendors'; // Vendor Management
import { Subscription } from './pages/Subscription';
import { ManageAccess } from './pages/ManageAccess';
import { FormTemplates } from './pages/FormTemplates';
import { FormTemplateEditor } from './pages/FormTemplateEditor';
import { CustomerFormReview } from './pages/CustomerFormReview';
import { CompanySettings } from './pages/CompanySettings';
import PhoneNumbers from './pages/PhoneNumbers';
import PhoneSetup from './pages/PhoneSetup';
import CommunicationsSettings from './pages/CommunicationsSettings';
import CustomFieldSettings from './pages/CustomFieldSettings';
import WorkflowSettings from './pages/WorkflowSettings';
import JobCostingSettings from './pages/JobCostingSettings';
import { ServiceTitanJobs } from './pages/ServiceTitanJobs';
import { Exceptions } from './pages/Exceptions';
import { AdminLogin, AdminFeedback, AdminTenants, AdminStripeTest, AdminSupportSessionPage, AdminSupport, AdminPurgeAudit, AdminPlatformRevenue, AdminDashboard, AdminTeam, AdminAcceptInvite, AdminMarketing, AdminCleanupQueue, AdminTwilioWebhookStatus, AdminReservationReset } from './pages/admin';
import AdminLayout from './components/AdminLayout';
import SupportBubble from './components/SupportBubble';
import { Softphone } from './components/Softphone';
import { billingAPI } from './lib/api';
// Debug components - only imported in development
const DialogLayerDebug = process.env.NODE_ENV === 'development' 
  ? require('./pages/_debug/DialogLayerDebug').default 
  : null;
// Scanner test page - available in all environments for testing
import ScannerTest from './pages/_debug/ScannerTest';
import { Profile } from './pages/Profile';
import { ReferralCenter } from './pages/ReferralCenter';
import { Billing } from './pages/Billing';
import { Reports } from './pages/Reports';
import ScheduledReports from './pages/ScheduledReports';
import { Tools } from './pages/Tools';
import { CycleCount } from './pages/CycleCount';
import LegalAccept from './pages/LegalAccept';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import ProjectDetail from './pages/ProjectDetail';
import Dispatch from './pages/Dispatch';
import SalesPipeline from './pages/SalesPipeline';
import Estimates from './pages/Estimates';
import EstimateEditor from './pages/EstimateEditor';
import MaintenancePlans from './pages/MaintenancePlans';
import EstimateApproval from './pages/EstimateApproval';
import Invoices from './pages/Invoices';
import InvoiceEditor from './pages/InvoiceEditor';
import InvoicePublicView from './pages/InvoicePublicView';
import PriceBook from './pages/PriceBook';
import HCPDeductions from './pages/HCPDeductions';
import PriceBookItemEditor from './pages/PriceBookItemEditor';
import UsageReport from './pages/UsageReport';
import FleetHealth from './pages/FleetHealth';
import TruckPrep from './pages/TruckPrep';
import ImportHistory from './pages/Imports/ImportHistory';
import ImportReview from './pages/Imports/ImportReview';
import MigrationWizard from './pages/MigrationWizard';
import WeeklyTruckReport from './pages/reports/WeeklyTruckReport';
import TechnicianScorecards from './pages/TechnicianScorecards';
import JobCostingReport from './pages/JobCostingReport';
import CommissionReport from './pages/CommissionReport';
import ProjectFinancials from './pages/ProjectFinancials';
import MarketingReviews from './pages/MarketingReviews';
import Campaigns from './pages/Campaigns';
import CampaignBuilder from './pages/CampaignBuilder';
import Audiences from './pages/Audiences';
import Automations from './pages/Automations';
import LeadSourceReport from './pages/LeadSourceReport';
import WebsiteServices from './pages/WebsiteServices';
import WebsiteChat from './pages/WebsiteChat';
import WebsiteChatInbox from './pages/WebsiteChatInbox';
import WebsiteChatAnalytics from './pages/WebsiteChatAnalytics';
import CallsInbox from './pages/CallsInbox';
import ContactCenterAnalytics from './pages/ContactCenterAnalytics';
import InventoryActivity from './pages/InventoryActivity';
import PublicTechProfile from './pages/PublicTechProfile';
import TechLayout from './pages/tech/TechLayout';
import TechHome from './pages/tech/TechHome';
import TechSchedule from './pages/tech/TechSchedule';
import TechJobDetail from './pages/tech/TechJobDetail';
import TechEstimateDetail from './pages/tech/TechEstimateDetail';
import TechEstimateEdit from './pages/tech/TechEstimateEdit';
import TechMore from './pages/tech/TechMore';
import TechMessages from './pages/tech/TechMessages';
import TechProfile from './pages/tech/TechProfile';
import TechMyVehicle from './pages/tech/TechMyVehicle';
import TechNewLead from './pages/tech/TechNewLead';
import TechNewEstimate from './pages/tech/TechNewEstimate';
import TechNewInvoice from './pages/tech/TechNewInvoice';
import { Loader2 } from 'lucide-react';

const ProtectedRoute = ({ children, ownerOnly = false, requiredPerm = null, requiredEntitlement = null, requiredEntitlementAny = null, requiredAddon = null, allowWhenLocked = false }) => {
  const { isAuthenticated, loading, isOwner, isTech, assignedVanId, hasPerm, hasEntitlement, hasAddon, user } = useAuth();
  const location = useLocation();
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);

  // Fetch billing status once on mount
  useEffect(() => {
    let cancelled = false;
    const checkBilling = async () => {
      // Skip billing check for allowed pages
      if (allowWhenLocked) {
        setBillingLoading(false);
        return;
      }
      
      try {
        const response = await billingAPI.getBetaTrialStatus();
        if (!cancelled) setBillingStatus(response.data);
      } catch (error) {
        // If billing check fails, don't block the user — allow through
        console.error('Failed to fetch billing status:', error);
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    };
    
    if (isAuthenticated && !loading) {
      checkBilling();
    } else {
      setBillingLoading(false);
    }
    return () => { cancelled = true; };
  }, [isAuthenticated, loading, allowWhenLocked]);

  if (loading || billingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="protected-route-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Check email verification for users with email (username-only users are exempt)
  // Redirect unverified users to /verify-email
  if (user?.email && !user?.email_verified_at) {
    return <Navigate to="/verify-email" replace />;
  }

  // Check billing lockout (trial expired, no subscription)
  // Redirect to /billing so user can subscribe — not /subscription
  if (!allowWhenLocked && billingStatus?.billing_required) {
    return <Navigate to="/billing" replace state={{ from: location }} />;
  }

  // Check permission-based access
  if (requiredPerm && !hasPerm(requiredPerm)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check entitlement-based access (module gating)
  if (requiredEntitlement && !hasEntitlement(requiredEntitlement)) {
    return <Navigate to="/billing" replace state={{ 
      entitlement_required: requiredEntitlement,
      blocked_path: location.pathname,
    }} />;
  }

  // Check entitlement-based access with OR logic (any match grants access)
  if (requiredEntitlementAny && !requiredEntitlementAny.some(e => hasEntitlement(e))) {
    return <Navigate to="/billing" replace state={{ 
      entitlement_required: requiredEntitlementAny[0],
      blocked_path: location.pathname,
    }} />;
  }

  // Check addon-based access (e.g. phone system)
  if (requiredAddon && !hasAddon(requiredAddon)) {
    return <Navigate to="/billing" replace state={{ 
      addon_required: requiredAddon,
      blocked_path: location.pathname,
    }} />;
  }

  // Legacy ownerOnly check (for backwards compatibility)
  if (ownerOnly && !isOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, isTech } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAuthenticated) {
    // If we arrived here with a returnTo path (e.g., after token refresh), honor it
    const returnTo = location.state?.from?.pathname;
    if (returnTo && returnTo !== '/login' && returnTo !== '/') {
      return <Navigate to={returnTo} replace />;
    }
    return <Navigate to={isTech ? '/tech' : '/dashboard'} replace />;
  }

  return children;
};

const DefaultRedirect = () => {
  const { isTech } = useAuth();
  return <Navigate to={isTech ? '/tech' : '/dashboard'} replace />;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/trial-setup" element={<TrialSetup />} />
      <Route path="/trial-activated" element={<TrialActivated />} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/legal/accept" element={<LegalAccept />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/warehouse" element={<ProtectedRoute><Warehouse /></ProtectedRoute>} />
      <Route path="/warehouse/import" element={<ProtectedRoute ownerOnly><WarehouseImport /></ProtectedRoute>} />
      <Route path="/vans" element={<ProtectedRoute requiredPerm="fleet.view"><Vans /></ProtectedRoute>} />
      <Route path="/vans/:id" element={<ProtectedRoute><VanDetail /></ProtectedRoute>} />
      <Route path="/fleet-health" element={<ProtectedRoute requiredPerm="fleet.view"><FleetHealth /></ProtectedRoute>} />
      <Route path="/my-vehicle" element={<ProtectedRoute><MyVehicle /></ProtectedRoute>} />
      <Route path="/catalogue" element={<ProtectedRoute requiredPerm="catalogue.view"><Catalogue /></ProtectedRoute>} />
      <Route path="/catalogue/duplicates" element={<ProtectedRoute ownerOnly><FindDuplicates /></ProtectedRoute>} />
      <Route path="/catalogue/import" element={<ProtectedRoute requiredPerm="catalogue.import_csv"><InventoryImport /></ProtectedRoute>} />
      <Route path="/import-history" element={<ProtectedRoute ownerOnly><ImportHistory /></ProtectedRoute>} />
      <Route path="/import-review/:sessionId" element={<ProtectedRoute ownerOnly><ImportReview /></ProtectedRoute>} />
      <Route path="/reorder" element={<Navigate to="/replenishment?view=needs-attention" replace />} />
      <Route path="/activity" element={<Navigate to="/inventory/activity" replace />} />
      <Route path="/inventory/activity" element={<ProtectedRoute requiredPerm="activity.view"><InventoryActivity /></ProtectedRoute>} />
      <Route path="/events/:id" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute requiredPerm="messages.view"><Messages /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute requiredPerm="team.view"><Team /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute requiredPerm="approvals.view"><Approvals /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute ownerOnly><Compare /></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute requiredPerm="po.view"><PurchaseOrders /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute requiredPerm="po.view"><Vendors /></ProtectedRoute>} /> {/* Vendor Management */}
      <Route path="/part-requests" element={<ProtectedRoute requiredPerm="po.view"><PartRequests /></ProtectedRoute>} /> {/* Phase 5A */}
      <Route path="/quarantine" element={<ProtectedRoute requiredPerm="inventory_tools.full_access"><Quarantine /></ProtectedRoute>} /> {/* Phase 5B */}
      <Route path="/job-timeline" element={<ProtectedRoute requiredPerm="po.view"><JobTimeline /></ProtectedRoute>} /> {/* Phase 5C */}
      <Route path="/job-kits" element={<ProtectedRoute requiredPerm="po.view"><JobKits /></ProtectedRoute>} /> {/* Phase 6C */}
      <Route path="/job-templates" element={<ProtectedRoute requiredEntitlement="crm"><JobTemplates /></ProtectedRoute>} />
      <Route path="/forms" element={<ProtectedRoute requiredEntitlement="crm"><FormTemplates /></ProtectedRoute>} />
      <Route path="/forms/:id" element={<ProtectedRoute requiredEntitlement="crm"><FormTemplateEditor /></ProtectedRoute>} />
      {/* Customer Form Review & Signature V1 — public, token-scoped, no auth */}
      <Route path="/customer/forms/:token" element={<CustomerFormReview />} />
      <Route path="/subscription" element={<ProtectedRoute ownerOnly allowWhenLocked><Subscription /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute allowWhenLocked><Profile /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute requiredPerm="referrals.view_team"><ReferralCenter /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute requiredPerm="billing.view" allowWhenLocked><Billing /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute requiredPerm="reports.view"><Reports /></ProtectedRoute>} />
      <Route path="/scheduled-reports" element={<ProtectedRoute requiredPerm="reports.view"><ScheduledReports /></ProtectedRoute>} />
      <Route path="/technician-scorecards" element={<ProtectedRoute requiredPerm="reports.view"><TechnicianScorecards /></ProtectedRoute>} />
      <Route path="/reports/job-costing" element={<ProtectedRoute requiredPerm="reports.view"><JobCostingReport /></ProtectedRoute>} />
      <Route path="/reports/commissions" element={<ProtectedRoute requiredPerm="reports.view"><CommissionReport /></ProtectedRoute>} />
      <Route path="/reports/project-financials" element={<ProtectedRoute requiredEntitlement="crm"><ProjectFinancials /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
      <Route path="/cycle-count/:id" element={<ProtectedRoute requiredPerm="audit.manage"><CycleCount /></ProtectedRoute>} />
      {/* Legacy route redirect for bookmarks */}
      <Route path="/audit/:id" element={<Navigate to={window.location.pathname.replace('/audit/', '/cycle-count/')} replace />} />
      <Route path="/manage-access" element={<ProtectedRoute requiredPerm="settings.manage_access"><ManageAccess /></ProtectedRoute>} />
      <Route path="/company-settings" element={<ProtectedRoute requiredPerm="settings.branding_edit"><CompanySettings /></ProtectedRoute>} />
      <Route path="/settings/phone-numbers" element={<ProtectedRoute><PhoneNumbers /></ProtectedRoute>} />
      <Route path="/settings/communications" element={<ProtectedRoute><CommunicationsSettings /></ProtectedRoute>} />
      <Route path="/settings/custom-fields" element={<ProtectedRoute requiredEntitlement="crm"><CustomFieldSettings /></ProtectedRoute>} />
      <Route path="/settings/workflows" element={<ProtectedRoute requiredEntitlement="crm"><WorkflowSettings /></ProtectedRoute>} />
      <Route path="/settings/job-costing" element={<ProtectedRoute requiredEntitlement="crm"><JobCostingSettings /></ProtectedRoute>} />
      <Route path="/phone-setup" element={<ProtectedRoute><PhoneSetup /></ProtectedRoute>} />
      <Route path="/servicetitan/jobs" element={<ProtectedRoute ownerOnly><ServiceTitanJobs /></ProtectedRoute>} />
      <Route path="/replenishment" element={<ProtectedRoute requiredPerm="inventory_tools.full_access"><Replenishment /></ProtectedRoute>} />
      <Route path="/alerts" element={<Navigate to="/replenishment" replace />} />
      <Route path="/exceptions" element={<ProtectedRoute requiredPerm="inventory_tools.full_access"><Exceptions /></ProtectedRoute>} />
      {/* Stage 1: CRM Routes - gated by crm entitlement */}
      <Route path="/customers" element={<ProtectedRoute requiredEntitlement="crm"><Customers /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute requiredEntitlement="crm"><CustomerDetail /></ProtectedRoute>} />
      <Route path="/sales-pipeline" element={<ProtectedRoute requiredEntitlement="crm"><SalesPipeline /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute requiredEntitlementAny={['crm', 'hcp_jobs']}><Jobs /></ProtectedRoute>} />
      <Route path="/jobs/projects/:id" element={<ProtectedRoute requiredEntitlement="crm"><ProjectDetail /></ProtectedRoute>} />
      <Route path="/jobs/:id" element={<ProtectedRoute requiredEntitlementAny={['crm', 'hcp_jobs']}><JobDetail /></ProtectedRoute>} />
      <Route path="/migration-wizard" element={<ProtectedRoute ownerOnly><MigrationWizard /></ProtectedRoute>} />
      <Route path="/dispatch" element={<ProtectedRoute requiredEntitlement="crm"><Dispatch /></ProtectedRoute>} />
      <Route path="/estimates" element={<ProtectedRoute requiredEntitlement="crm"><Estimates /></ProtectedRoute>} />
      <Route path="/estimates/:id" element={<ProtectedRoute requiredEntitlement="crm"><EstimateEditor /></ProtectedRoute>} />
      <Route path="/approve/:token" element={<EstimateApproval />} />
      <Route path="/invoices" element={<ProtectedRoute requiredEntitlement="crm"><Invoices /></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute requiredEntitlement="crm"><InvoiceEditor /></ProtectedRoute>} />
      <Route path="/maintenance-plans" element={<ProtectedRoute requiredEntitlement="crm"><MaintenancePlans /></ProtectedRoute>} />
      <Route path="/price-book" element={<ProtectedRoute><PriceBook /></ProtectedRoute>} />
      <Route path="/price-book/:id" element={<ProtectedRoute requiredEntitlement="crm"><PriceBookItemEditor /></ProtectedRoute>} />
      <Route path="/hcp-deductions" element={<ProtectedRoute><HCPDeductions /></ProtectedRoute>} />
      <Route path="/invoice/:token" element={<InvoicePublicView />} />
      <Route path="/technician/:token" element={<PublicTechProfile />} />
      {/* Mobile Technician View */}
      <Route path="/tech" element={<ProtectedRoute><TechLayout /></ProtectedRoute>}>
        <Route index element={<TechHome />} />
        <Route path="schedule" element={<TechSchedule />} />
        <Route path="jobs/:eventId" element={<TechJobDetail />} />
        <Route path="estimates/:estimateId" element={<TechEstimateDetail />} />
                <Route path="estimates/:estimateId/edit" element={<TechEstimateEdit />} />
        <Route path="messages" element={<TechMessages />} />
        <Route path="profile" element={<TechProfile />} />
        <Route path="my-vehicle" element={<TechMyVehicle />} />
        <Route path="new-lead" element={<TechNewLead />} />
        <Route path="new-estimate" element={<TechNewEstimate />} />
        <Route path="new-invoice" element={<TechNewInvoice />} />
        <Route path="more" element={<TechMore />} />
      </Route>
      {/* Legacy public tech profile URL — kept for old SMS/email links */}
      <Route path="/tech/:token" element={<PublicTechProfile />} />
      <Route path="/truck-prep" element={<ProtectedRoute requiredEntitlement="crm"><TruckPrep /></ProtectedRoute>} />
      <Route path="/weekly-truck-report" element={<ProtectedRoute requiredEntitlement="crm"><WeeklyTruckReport /></ProtectedRoute>} />
      <Route path="/usage-report" element={<ProtectedRoute requiredEntitlement="crm"><UsageReport /></ProtectedRoute>} />
      {/* Marketing Center */}
      <Route path="/marketing/reviews" element={<ProtectedRoute><MarketingReviews /></ProtectedRoute>} />
      <Route path="/marketing/campaigns" element={<ProtectedRoute requiredEntitlement="crm"><Campaigns /></ProtectedRoute>} />
      <Route path="/marketing/campaigns/:id" element={<ProtectedRoute requiredEntitlement="crm"><CampaignBuilder /></ProtectedRoute>} />
      <Route path="/marketing/audiences" element={<ProtectedRoute requiredEntitlement="crm"><Audiences /></ProtectedRoute>} />
      <Route path="/marketing/automations" element={<ProtectedRoute requiredEntitlement="crm"><Automations /></ProtectedRoute>} />
      <Route path="/marketing/lead-report" element={<ProtectedRoute requiredEntitlement="crm"><LeadSourceReport /></ProtectedRoute>} />
      <Route path="/marketing/website" element={<ProtectedRoute><WebsiteServices /></ProtectedRoute>} />
      <Route path="/marketing/website-chat" element={<ProtectedRoute><WebsiteChat /></ProtectedRoute>} />
      <Route path="/marketing/website-chat/inbox" element={<ProtectedRoute><WebsiteChatInbox /></ProtectedRoute>} />
      <Route path="/marketing/website-chat/analytics" element={<ProtectedRoute><WebsiteChatAnalytics /></ProtectedRoute>} />
      <Route path="/calls" element={<Navigate to="/contact-center" replace />} />
      <Route path="/contact-center" element={<ProtectedRoute requiredAddon="phone_system"><CallsInbox /></ProtectedRoute>} />
      <Route path="/contact-center/analytics" element={<ProtectedRoute requiredAddon="phone_system"><ContactCenterAnalytics /></ProtectedRoute>} />
      {/* Redirect old inventory route to catalogue */}
      <Route path="/inventory" element={<Navigate to="/catalogue" replace />} />
      <Route path="/inventory/import" element={<Navigate to="/catalogue/import" replace />} />

      {/* Admin routes (separate from tenant auth) */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/accept-invite" element={<AdminAcceptInvite />} />
      <Route element={<AdminLayout />}>
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/feedback" element={<AdminFeedback />} />
        <Route path="/admin/tenants" element={<AdminTenants />} />
        <Route path="/admin/team" element={<AdminTeam />} />
        <Route path="/admin/stripe-test" element={<AdminStripeTest />} />
        <Route path="/admin/support/session/:sessionId" element={<AdminSupportSessionPage />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/purge-audit" element={<AdminPurgeAudit />} />
        <Route path="/admin/platform-revenue" element={<AdminPlatformRevenue />} />
        <Route path="/admin/marketing" element={<AdminMarketing />} />
        <Route path="/admin/cleanup-queue" element={<AdminCleanupQueue />} />
        <Route path="/admin/twilio-webhooks" element={<AdminTwilioWebhookStatus />} />
        <Route path="/admin/maintenance/reservation-reset/:tenantId" element={<AdminReservationReset />} />
      </Route>
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      
      {/* Debug routes (dev only) */}
      {process.env.NODE_ENV === 'development' && DialogLayerDebug && (
        <Route path="/_debug/dialog" element={<DialogLayerDebug />} />
      )}
      {/* Scanner test route - available for testing */}
      <Route path="/_debug/scanner" element={<ScannerTest />} />

      <Route path="/" element={<DefaultRedirect />} />
      <Route path="/items" element={<Navigate to="/inventory" replace />} />
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
}

// Wrapper to show SupportBubble only on authenticated non-admin pages
const SupportBubbleWrapper = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Don't show on admin pages, tech mobile view, or when not authenticated
  if (!isAuthenticated || location.pathname.startsWith('/admin') || location.pathname.startsWith('/tech')) {
    return null;
  }
  
  return <SupportBubble />;
};

// Wrapper to show Softphone only for authenticated users with CRM entitlement
const SoftphoneWrapper = () => {
  const { isAuthenticated, hasEntitlement } = useAuth();
  const location = useLocation();

  if (!isAuthenticated || location.pathname.startsWith('/admin') || location.pathname.startsWith('/approve') || location.pathname.startsWith('/tech')) {
    return null;
  }

  // Only show if tenant has CRM entitlement (voice features)
  if (hasEntitlement && !hasEntitlement('crm')) {
    return null;
  }

  return <Softphone />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ImpersonationProvider>
          <SupportSessionProvider>
            <SupportProvider>
              <WarehouseProvider>
                <UnreadMessagesProvider>
                  {/* App-wide tooltip provider so any TruncatedTextWithTooltip
                      anywhere in the tree gets keyboard + hover tooltips
                      without needing local providers. Existing nested
                      providers (e.g. Layout's sidebar) remain harmless. */}
                  <TooltipProvider delayDuration={200} skipDelayDuration={400}>
                    <ImpersonationBanner />
                    <SupportSessionBanner />
                    <AppRoutes />
                    <SoftphoneWrapper />
                    <SupportBubbleWrapper />
                    <Toaster position="top-center" richColors />
                  </TooltipProvider>
                </UnreadMessagesProvider>
              </WarehouseProvider>
            </SupportProvider>
          </SupportSessionProvider>
        </ImpersonationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
